import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import mapboxgl from 'mapbox-gl';
import MapboxGeocoder from '@mapbox/mapbox-gl-geocoder';
export const useGoogleMapsAutoCompleteInput = (
  onChange: (event: string | Record<string, unknown>) => void,
) => {
  const autoCompleteRef = useRef<HTMLInputElement | null>(null);
  const [t] = useTranslation();

  const extractFromMapboxResult = (result: {
    place_name?: string;
    center?: [number, number];
    context?: { id?: string; text?: string }[];
    text?: string;
    place_type?: string[];
  }) => {
    const location = result.place_name || result.text || '';
    const center = result.center;
    const lng = center?.[0];
    const lat = center?.[1];

    const contextByType = (result.context || []).reduce<Record<string, string>>((acc, c) => {
      const id = c.id || '';
      const type = id.split('.')[0];
      if (type && c.text) acc[type] = c.text;
      return acc;
    }, {});

    const isCountryResult = result.place_type?.includes('country');
    const isRegionResult = result.place_type?.includes('region');
    const isPlaceResult = result.place_type?.includes('place');
    const isLocalityResult = result.place_type?.includes('locality');

    const country =
      contextByType.country ||
      (isCountryResult ? result.text : undefined);

    const state =
      contextByType.place ||
      contextByType.locality ||
      contextByType.district ||
      contextByType.neighborhood ||
      contextByType.region ||
      (isPlaceResult || isLocalityResult || isRegionResult ? result.text : undefined);

    return { location, lng, lat, country, state };
  };

  const handleChange = (event: string) => {
    onChange(event);
  };

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const token = process.env.RAZZLE_MAPBOX_ACCESS_TOKEN || process.env.MAPBOX_ACCESS_TOKEN;
    if (!token) return;
    if (!autoCompleteRef.current) return;

    mapboxgl.accessToken = token;

    const input = autoCompleteRef.current;
    const parent = input.parentElement;
    if (!parent) return;

    const geocoder = new MapboxGeocoder({
      accessToken: token,
      mapboxgl,
      marker: false,
      placeholder: t('addVideoDashboard.countryAndCity'),
      types: 'country,region,place,locality,address',
    });

    input.style.display = 'none';
    const geocoderEl = geocoder.onAdd(null as unknown as mapboxgl.Map) as unknown as Node;
    parent.appendChild(geocoderEl);

    geocoder.on('result', (event: { result?: any }) => {
      const result = event?.result;
      if (!result) return;
      const extracted = extractFromMapboxResult(result);
      if (extracted.lat === undefined || extracted.lng === undefined) return;
      onChange(extracted as unknown as Record<string, unknown>);
    });

    return () => {
      try {
        geocoder.clear();
      } catch (_e) {
        // ignore
      }
      try {
        parent.removeChild(geocoderEl);
      } catch (_e) {
        // ignore
      }
      input.style.display = '';
    };
  }, [onChange, t]);

  return { handleChange, autoCompleteRef, t };
};
